import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface CookieConsent {
  analytics: boolean;
  necessary: boolean;
  timestamp: number;
}

interface CookieConsentContextType {
  consent: CookieConsent | null;
  hasConsented: boolean;
  isEuropeanUser: boolean;
  showBanner: boolean;
  acceptAll: () => void;
  acceptNecessary: () => void;
  acceptAnalytics: () => void;
  rejectAnalytics: () => void;
  updateConsent: (consent: Partial<CookieConsent>) => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const CONSENT_STORAGE_KEY = 'dombase_cookie_consent';
const CONSENT_EXPIRY_DAYS = 365;

// European Union country codes
const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
  'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
];

interface CookieConsentProviderProps {
  children: ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [isEuropeanUser, setIsEuropeanUser] = useState<boolean>(false);
  const [showBanner, setShowBanner] = useState<boolean>(false);

  // Check if user is in European Union
  const checkEuropeanUser = async (): Promise<boolean> => {
    try {
      // Try to get country from IP geolocation
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      return EU_COUNTRIES.includes(data.country_code);
    } catch (error) {
      console.warn('Could not determine user location, assuming non-EU for safety');
      return false;
    }
  };

  // Load consent from localStorage
  const loadConsent = (): CookieConsent | null => {
    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (!stored) return null;

      const parsed = JSON.parse(stored) as CookieConsent;
      const now = Date.now();
      const expiryTime = parsed.timestamp + (CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000);

      // Check if consent has expired
      if (now > expiryTime) {
        localStorage.removeItem(CONSENT_STORAGE_KEY);
        return null;
      }

      return parsed;
    } catch (error) {
      console.error('Error loading cookie consent:', error);
      return null;
    }
  };

  // Save consent to localStorage
  const saveConsent = (newConsent: CookieConsent): void => {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(newConsent));
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    }
  };

  // Initialize consent state
  useEffect(() => {
    const initializeConsent = async () => {
      const storedConsent = loadConsent();
      const isEU = await checkEuropeanUser();

      setIsEuropeanUser(isEU);
      setConsent(storedConsent);

      // Show banner if user is European and hasn't consented yet
      if (isEU && !storedConsent) {
        setShowBanner(true);
      }
    };

    initializeConsent();
  }, []);

  const hasConsented = consent !== null;

  const acceptAll = () => {
    const newConsent: CookieConsent = {
      analytics: true,
      necessary: true,
      timestamp: Date.now()
    };
    setConsent(newConsent);
    saveConsent(newConsent);
    setShowBanner(false);
  };

  const acceptNecessary = () => {
    const newConsent: CookieConsent = {
      analytics: false,
      necessary: true,
      timestamp: Date.now()
    };
    setConsent(newConsent);
    saveConsent(newConsent);
    setShowBanner(false);
  };

  const acceptAnalytics = () => {
    if (consent) {
      const newConsent: CookieConsent = {
        ...consent,
        analytics: true,
        timestamp: Date.now()
      };
      setConsent(newConsent);
      saveConsent(newConsent);
    }
  };

  const rejectAnalytics = () => {
    if (consent) {
      const newConsent: CookieConsent = {
        ...consent,
        analytics: false,
        timestamp: Date.now()
      };
      setConsent(newConsent);
      saveConsent(newConsent);
    }
  };

  const updateConsent = (newConsent: Partial<CookieConsent>) => {
    if (consent) {
      const updatedConsent: CookieConsent = {
        ...consent,
        ...newConsent,
        timestamp: Date.now()
      };
      setConsent(updatedConsent);
      saveConsent(updatedConsent);
    }
  };

  const value: CookieConsentContextType = {
    consent,
    hasConsented,
    isEuropeanUser,
    showBanner,
    acceptAll,
    acceptNecessary,
    acceptAnalytics,
    rejectAnalytics,
    updateConsent
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};
